import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Code2, ChevronDown, ChevronRight, Key, ExternalLink } from 'lucide-react';
import { publicDocsPath } from '@/lib/publicDocsSite';
import { CopyButton } from '../../../../components/ds';

interface Endpoint {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  path: string;
  description: string;
  scopes?: string;
  params?: { name: string; type: string; required?: boolean; desc: string }[];
  body?: { field: string; type: string; required?: boolean; desc: string }[];
  example?: { request?: string; response?: string };
}

interface Section {
  id: string;
  label: string;
  description: string;
  endpoints: Endpoint[];
}

const methodColors: Record<string, string> = {
  GET: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  POST: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  PATCH: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  DELETE: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  PUT: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
};

const sections: Section[] = [
  {
    id: 'authentication',
    label: 'Authentication',
    description: 'All requests require a Bearer API key. Keys are workspace-scoped and created in Settings → API Keys.',
    endpoints: [],
  },
  {
    id: 'items',
    label: 'Items',
    description: 'Items are the primary work objects. They exist at the workspace level and participate in cycles, sessions, and relationships.',
    endpoints: [
      {
        method: 'GET', path: '/v1/items', description: 'List items in the workspace.',
        scopes: 'items:read',
        params: [
          { name: 'status', type: 'string', desc: 'Filter by item status' },
          { name: 'category', type: 'string', desc: 'Filter by item category' },
          { name: 'search', type: 'string', desc: 'Case-insensitive name search' },
          { name: 'page', type: 'integer', desc: 'Page number (default: 1)' },
          { name: 'per_page', type: 'integer', desc: 'Results per page (max 100)' },
        ],
        example: {
          request: `curl "https://<project>.supabase.co/functions/v1/api/v1/items?status=active&per_page=25" \\
  -H "Authorization: Bearer pk_live_..."`,
          response: `{
  "data": [
    {
      "id": "a1b2c3d4-...",
      "name": "Improve onboarding flow",
      "status": "active",
      "category": "initiative"
    }
  ],
  "meta": { "workspace_id": "...", "request_id": "..." },
  "pagination": { "page": 1, "per_page": 25, "total": 87, "total_pages": 4 }
}`,
        },
      },
      {
        method: 'POST', path: '/v1/items', description: 'Create a new item.',
        scopes: 'items:write',
        body: [
          { field: 'name', type: 'string', required: true, desc: 'Item name' },
          { field: 'description', type: 'string', desc: 'Item description' },
          { field: 'category', type: 'string', desc: 'Item category' },
          { field: 'status', type: 'string', desc: 'Lifecycle status' },
          { field: 'metadata', type: 'object', desc: 'Additional metadata' },
        ],
        example: {
          request: `curl -X POST "https://<project>.supabase.co/functions/v1/api/v1/items" \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "name": "Migrate to new infrastructure", "category": "initiative" }'`,
        },
      },
      { method: 'GET', path: '/v1/items/:id', description: 'Get a single item by ID.', scopes: 'items:read' },
      { method: 'PATCH', path: '/v1/items/:id', description: 'Partial update — only fields present in the body are changed.', scopes: 'items:write' },
      { method: 'DELETE', path: '/v1/items/:id', description: 'Delete an item.', scopes: 'items:write' },
      { method: 'GET', path: '/v1/items/:id/relationships', description: 'List all relationships where this item is source or target.', scopes: 'items:read' },
      { method: 'GET', path: '/v1/items/:id/attributes', description: 'Get all attribute frames for an item.', scopes: 'items:read' },
      { method: 'PATCH', path: '/v1/items/:id/attributes', description: 'Upsert attribute frames. Existing frames with the same frame_type are replaced.', scopes: 'items:write' },
    ],
  },
  {
    id: 'sessions',
    label: 'Sessions',
    description: 'Prioritization sessions are the facilitated units of ranking work. Sessions own criteria, eligibility, tool configuration, and outputs.',
    endpoints: [
      { method: 'GET', path: '/v1/sessions', description: 'List sessions.', scopes: 'sessions:read',
        params: [
          { name: 'cycle_id', type: 'uuid', desc: 'Filter by cycle' },
          { name: 'state', type: 'string', desc: 'Filter by session state (e.g. DRAFT, PUBLISHED)' },
          { name: 'page', type: 'integer', desc: 'Page number' },
          { name: 'per_page', type: 'integer', desc: 'Results per page' },
        ],
      },
      { method: 'POST', path: '/v1/sessions', description: 'Create a session.', scopes: 'sessions:write',
        body: [
          { field: 'name', type: 'string', desc: 'Session name' },
          { field: 'cycle_id', type: 'uuid', desc: 'Owning cycle' },
          { field: 'facilitator_id', type: 'uuid', desc: 'Facilitating user' },
          { field: 'criteria_config', type: 'object', desc: 'Criteria configuration' },
          { field: 'mode_config', type: 'object', desc: 'Tool mode configuration' },
        ],
      },
      { method: 'GET', path: '/v1/sessions/:id', description: 'Get a session by ID.', scopes: 'sessions:read' },
      { method: 'PATCH', path: '/v1/sessions/:id', description: 'Update a session.', scopes: 'sessions:write' },
      { method: 'GET', path: '/v1/sessions/:id/results', description: 'Get all result records for a session.', scopes: 'sessions:read' },
      {
        method: 'POST', path: '/v1/sessions/:id/transition', description: 'Transition a session to a new state.',
        scopes: 'sessions:write',
        body: [{ field: 'to_state', type: 'string', required: true, desc: 'Target state (e.g. RUN_COMPLETE, PUBLISHED)' }],
        example: {
          request: `curl -X POST ".../v1/sessions/abc.../transition" \\
  -H "Authorization: Bearer pk_live_..." \\
  -d '{ "to_state": "PUBLISHED" }'`,
          response: `{ "data": { "id": "abc...", "state": "PUBLISHED" }, "meta": { ... } }`,
        },
      },
    ],
  },
  {
    id: 'tool-sessions',
    label: 'Tool Sessions',
    description: 'Collaborative runs of a single prioritization tool within a session. Lifecycle: collecting → revealed → finalized.',
    endpoints: [
      { method: 'GET', path: '/v1/tool-sessions', description: 'List tool sessions.', scopes: 'tool_sessions:read',
        params: [
          { name: 'session_id', type: 'uuid', desc: 'Filter by parent session' },
          { name: 'phase', type: 'string', desc: 'Filter by phase: collecting, revealed, finalized' },
        ],
      },
      { method: 'POST', path: '/v1/tool-sessions', description: 'Create a tool session.', scopes: 'tool_sessions:write',
        body: [
          { field: 'session_id', type: 'uuid', required: true, desc: 'Parent prioritization session' },
          { field: 'tool_type', type: 'string', required: true, desc: 'Registry tool ID (e.g. rice, dot_voting)' },
          { field: 'tool_name', type: 'string', desc: 'Display name' },
          { field: 'facilitator_id', type: 'uuid', required: true, desc: 'Facilitating user' },
          { field: 'items', type: 'array', desc: 'Item snapshots [{ id, name, description }]' },
          { field: 'participant_ids', type: 'array', desc: 'Expected participant user IDs' },
          { field: 'vote_mode', type: 'string', desc: '"hidden" (default) or "live"' },
        ],
      },
      { method: 'GET', path: '/v1/tool-sessions/:id', description: 'Get a tool session with all participant responses inline.', scopes: 'tool_sessions:read' },
      { method: 'PATCH', path: '/v1/tool-sessions/:id', description: 'Update phase, vote_mode, or item list.', scopes: 'tool_sessions:write' },
      {
        method: 'POST',
        path: '/v1/tool-sessions/:id/responses',
        description: 'Upsert a participant response (participant_id required). Completed responses drive aggregation on finalize.',
        scopes: 'tool_sessions:write',
      },
      {
        method: 'POST',
        path: '/v1/tool-sessions/:id/finalize',
        description: 'Set phase to finalized and compute aggregate_result from completed responses; optional facilitator_overrides in body.',
        scopes: 'tool_sessions:write',
      },
    ],
  },
  {
    id: 'cycles',
    label: 'Cycles',
    description: 'Cycles are the governance containers for prioritization efforts. They contain track instances and produce published priority lists.',
    endpoints: [
      { method: 'GET', path: '/v1/cycles', description: 'List cycles.', scopes: 'cycles:read' },
      { method: 'POST', path: '/v1/cycles', description: 'Create a cycle.', scopes: 'cycles:write' },
      { method: 'GET', path: '/v1/cycles/:id', description: 'Get a cycle by ID.', scopes: 'cycles:read' },
      { method: 'PATCH', path: '/v1/cycles/:id', description: 'Update a cycle.', scopes: 'cycles:write' },
      { method: 'GET', path: '/v1/cycles/:id/tracks', description: 'List track instances for a cycle.', scopes: 'cycles:read' },
      { method: 'GET', path: '/v1/cycles/:id/results', description: 'Get the published priority list for a cycle.', scopes: 'cycles:read' },
    ],
  },
  {
    id: 'catchball',
    label: 'Catchball',
    description: 'Bidirectional proposals and loop state; transitions use RPC-backed routes.',
    endpoints: [
      {
        method: 'GET',
        path: '/v1/catchball/loops',
        description: 'List catchball loops.',
        scopes: 'catchball:read',
        params: [{ name: 'cycle_id', type: 'uuid', desc: 'Filter by cycle' }],
      },
      { method: 'GET', path: '/v1/catchball/loops/:id', description: 'Get a catchball loop.', scopes: 'catchball:read' },
      {
        method: 'GET',
        path: '/v1/catchball/proposals',
        description: 'List proposals.',
        scopes: 'catchball:read',
        params: [
          { name: 'cycle_id', type: 'uuid', desc: 'Filter by cycle' },
          { name: 'loop_id', type: 'uuid', desc: 'Filter by loop' },
          { name: 'status', type: 'string', desc: 'Filter by status' },
          { name: 'submitter_id', type: 'uuid', desc: 'Filter by proposer (`proposer_id` query alias supported)' },
        ],
      },
      {
        method: 'POST',
        path: '/v1/catchball/proposals',
        description:
          'Submit packet (RPC catchball_submit_proposal): proposal_id/packet_id, loop_id, submitter_id (proposer_id alias); optional rationale, tradeoffs, content.',
        scopes: 'catchball:write',
      },
      { method: 'GET', path: '/v1/catchball/proposals/:id', description: 'Get a proposal.', scopes: 'catchball:read' },
      { method: 'POST', path: '/v1/catchball/proposals/:id/accept', description: 'Accept proposal, optionally provisional or with conditions (RPC: catchball_accept).', scopes: 'catchball:write' },
      { method: 'POST', path: '/v1/catchball/proposals/:id/reject', description: 'Reject proposal.', scopes: 'catchball:write' },
      { method: 'POST', path: '/v1/catchball/proposals/:id/return', description: 'Return proposal for revision.', scopes: 'catchball:write' },
      {
        method: 'GET',
        path: '/v1/catchball/threads',
        description: 'List thread entries for a proposal.',
        scopes: 'catchball:read',
        params: [{ name: 'proposal_id', type: 'uuid', desc: 'Required — proposal to load thread for', required: true }],
      },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    description: 'Workspace-level operations: metadata, settings, and API key management.',
    endpoints: [
      { method: 'GET', path: '/v1/workspace', description: 'Get workspace metadata.', scopes: 'workspace:read' },
      { method: 'GET', path: '/v1/workspace/api-keys', description: 'List API keys (hashed — raw key not recoverable).', scopes: 'workspace:admin' },
      { method: 'POST', path: '/v1/workspace/api-keys', description: 'Create a new API key. Raw key shown once in response.', scopes: 'workspace:admin',
        body: [
          { field: 'name', type: 'string', required: true, desc: 'Human-readable label for this key' },
          { field: 'scopes', type: 'array', required: true, desc: 'Array of scope strings this key is authorized for' },
          { field: 'expires_at', type: 'timestamp', desc: 'Optional expiration time' },
        ],
      },
      { method: 'DELETE', path: '/v1/workspace/api-keys/:id', description: 'Revoke an API key immediately.', scopes: 'workspace:admin' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    description: 'JSON reports by resource; CSV when Accept includes text/csv.',
    endpoints: [
      {
        method: 'GET',
        path: '/v1/reports/sessions/:id',
        description: 'Session detail plus session_results (CSV supported).',
        scopes: 'reports:read',
      },
      {
        method: 'GET',
        path: '/v1/reports/cycles/:id',
        description: 'Cycle, tracks for that cycle, and session summary rows (CSV supported).',
        scopes: 'reports:read',
      },
      {
        method: 'GET',
        path: '/v1/reports/items',
        description: 'Paginated workspace item list (?page, ?per_page; optional status, category filters; CSV supported).',
        scopes: 'reports:read',
        params: [
          { name: 'page', type: 'integer', desc: 'Page number' },
          { name: 'per_page', type: 'integer', desc: 'Page size (max 500)' },
          { name: 'status', type: 'string', desc: 'Filter by item status' },
          { name: 'category', type: 'string', desc: 'Filter by category' },
        ],
      },
    ],
  },
  {
    id: 'webhooks',
    label: 'Webhooks',
    description: 'Receive real-time events via HTTP POST. Register endpoints, choose event types, and validate signatures.',
    endpoints: [
      { method: 'GET', path: '/v1/webhooks', description: 'List registered webhook endpoints.', scopes: 'webhooks:read' },
      {
        method: 'POST',
        path: '/v1/webhooks',
        description: 'Register a webhook (stored as workspace_item_sources type webhook).',
        scopes: 'webhooks:write',
        body: [
          { field: 'name', type: 'string', required: true, desc: 'Label for this endpoint' },
          { field: 'url', type: 'string', required: true, desc: 'HTTPS URL to POST events to' },
          { field: 'events', type: 'array', desc: 'Event types filter; defaults to ["*"] when omitted' },
          { field: 'secret', type: 'string', desc: 'Optional signing secret' },
        ],
      },
      { method: 'DELETE', path: '/v1/webhooks/:id', description: 'Delete a webhook endpoint.', scopes: 'webhooks:write' },
    ],
  },
];


function EndpointCard({ endpoint }: { endpoint: Endpoint }) {
  const [open, setOpen] = useState(false);
  const hasDetails = endpoint.params || endpoint.body || endpoint.example;

  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--surface)] transition-colors"
        onClick={() => hasDetails && setOpen(!open)}
      >
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold font-mono flex-shrink-0 ${methodColors[endpoint.method]}`}>
          {endpoint.method}
        </span>
        <code className="text-sm font-mono text-[var(--text-pri)] flex-1">{endpoint.path}</code>
        <span className="text-sm text-[var(--text-ter)] flex-1 text-right pr-3 truncate hidden sm:block">{endpoint.description}</span>
        {endpoint.scopes && (
          <span className="text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-0.5 rounded font-mono flex-shrink-0 hidden md:block">{endpoint.scopes}</span>
        )}
        {hasDetails && (
          open ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="border-t border-[var(--rule)] px-4 py-4 space-y-4 bg-[var(--surface)]/50">
          <p className="text-sm text-[var(--text-sec)]">{endpoint.description}</p>

          {endpoint.params && (
            <div>
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Query Parameters</p>
              <div className="border border-[var(--rule)] overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[var(--hover-tint)]">
                      <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)] w-36">Parameter</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)] w-20">Type</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)]">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoint.params.map((p) => (
                      <tr key={p.name} className="border-t border-[var(--rule)] bg-[var(--bg-raised)]">
                        <td className="px-3 py-1.5 font-mono text-[var(--text-ter)]">{p.name}</td>
                        <td className="px-3 py-1.5 text-[var(--text-ter)]">{p.type}</td>
                        <td className="px-3 py-1.5 text-[var(--text-sec)]">{p.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {endpoint.body && (
            <div>
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Request Body</p>
              <div className="border border-[var(--rule)] overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[var(--hover-tint)]">
                      <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)] w-36">Field</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)] w-20">Type</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)] w-20">Required</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)]">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoint.body.map((f) => (
                      <tr key={f.field} className="border-t border-[var(--rule)] bg-[var(--bg-raised)]">
                        <td className="px-3 py-1.5 font-mono text-[var(--text-ter)]">{f.field}</td>
                        <td className="px-3 py-1.5 text-[var(--text-ter)]">{f.type}</td>
                        <td className="px-3 py-1.5">{f.required ? <span className="text-[var(--coral)] font-medium">yes</span> : <span className="text-[var(--text-ter)]">no</span>}</td>
                        <td className="px-3 py-1.5 text-[var(--text-sec)]">{f.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {endpoint.example && (
            <div className="space-y-2">
              {endpoint.example.request && (
                <div>
                  <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1">Example Request</p>
                  <div className="relative bg-[var(--bg)] border border-[var(--rule)]">
                    <div className="absolute top-2 right-2">
                      <CopyButton text={endpoint.example.request} />
                    </div>
                    <pre className="text-xs text-[var(--text-ter)] p-3 overflow-x-auto font-mono leading-relaxed">{endpoint.example.request}</pre>
                  </div>
                </div>
              )}
              {endpoint.example.response && (
                <div>
                  <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1">Example Response</p>
                  <div className="relative bg-[var(--bg)] border border-[var(--rule)]">
                    <div className="absolute top-2 right-2">
                      <CopyButton text={endpoint.example.response} />
                    </div>
                    <pre className="text-xs text-[var(--text-ter)] p-3 overflow-x-auto font-mono leading-relaxed">{endpoint.example.response}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApiReferenceDocs() {
  const [activeSection, setActiveSection] = useState('authentication');

  const scrollTo = (id: string) => {
    setActiveSection(id);
    document.getElementById(`api-section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-4">
      {/* Breadcrumb + header */}
      <div>
        <Link
          to="/prioritization/documentation"
          className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-primary-600 transition-colors mb-4"
        >
          <ChevronLeft className="h-3 w-3" />
          Documentation
        </Link>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 border border-[var(--rule)] bg-[var(--surface)] flex items-center justify-center flex-shrink-0">
            <Code2 className="h-4 w-4 text-[var(--text-sec)]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[var(--text-pri)] font-heading">API Reference</h1>
            <p className="text-sm text-[var(--text-ter)] mt-0.5">
              REST API for programmatic access. All resources are workspace-scoped. Canonical, maintained docs live on{' '}
              <a
                href={publicDocsPath('/api')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline inline-flex items-center gap-0.5"
              >
                priorities-ai-docs.vercel.app
                <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
              </a>{' '}
              — treat this page as a quick index aligned with the deployed edge API.
            </p>
          </div>
        </div>
      </div>

      {/* Layout: sticky nav + content */}
      <div className="flex gap-6 items-start">
        {/* Sticky nav */}
        <aside className="hidden lg:block flex-shrink-0 w-44 sticky top-6">
          <nav className="space-y-0.5">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide px-2 py-1.5">Sections</p>
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`w-full text-left px-2 py-1.5  text-sm transition-colors ${
                  activeSection === s.id
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-10">

          {/* Authentication section — special layout */}
          <section id="api-section-authentication">
            <div className="flex items-center gap-2 mb-3">
              <Key className="h-4 w-4 text-[var(--text-ter)]" />
              <h2 className="text-base font-semibold text-[var(--text-pri)] font-heading">Authentication</h2>
            </div>
            <p className="text-sm text-[var(--text-ter)] mb-4">All requests require a Bearer API key.</p>

            <div className="grid gap-4">
              {/* Base URL */}
              <div className="border border-[var(--rule)] p-4">
                <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Base URL</p>
                <div className="relative bg-[var(--bg)] border border-[var(--rule)]">
                  <div className="absolute top-2 right-2">
                    <CopyButton text="https://<project>.supabase.co/functions/v1/api/v1" />
                  </div>
                  <pre className="text-xs text-[var(--text-ter)] px-3 py-2 font-mono">https://&lt;project&gt;.supabase.co/functions/v1/api/v1</pre>
                </div>
              </div>

              {/* Key format */}
              <div className="border border-[var(--rule)] p-4">
                <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Authorization Header</p>
                <div className="relative bg-[var(--bg)] border border-[var(--rule)]">
                  <div className="absolute top-2 right-2">
                    <CopyButton text="Authorization: Bearer pk_live_<64 hex chars>" />
                  </div>
                  <pre className="text-xs text-[var(--text-ter)] px-3 py-2 font-mono">Authorization: Bearer pk_live_&lt;64 hex chars&gt;</pre>
                </div>
                <p className="text-xs text-[var(--text-ter)] mt-2">Keys are created in <Link to="/prioritization/settings/api-keys" className="text-primary-600 hover:underline">Settings → API Keys</Link> or via <code className="font-mono bg-[var(--hover-tint)] px-1 rounded">POST /v1/workspace/api-keys</code>. The raw key is shown once — only a SHA-256 hash is stored.</p>
              </div>

              {/* Error codes */}
              <div className="border border-[var(--rule)] p-4">
                <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Error Codes</p>
                <div className="border border-[var(--rule)] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-[var(--surface)]"><th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)] w-20">HTTP</th><th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)] w-32">Code</th><th className="text-left px-3 py-1.5 font-semibold text-[var(--text-sec)]">Meaning</th></tr></thead>
                    <tbody>
                      {[
                        { status: '400', code: 'BAD_REQUEST', desc: 'Missing or invalid parameters' },
                        { status: '401', code: 'UNAUTHORIZED', desc: 'Missing, invalid, revoked, or expired API key' },
                        { status: '403', code: 'FORBIDDEN', desc: 'API key does not have the required scope' },
                        { status: '404', code: 'NOT_FOUND', desc: 'Resource not found or not in this workspace' },
                        { status: '429', code: 'RATE_LIMITED', desc: '120 req/60s per key — back off and retry' },
                        { status: '500', code: 'INTERNAL_ERROR', desc: 'Unexpected server error' },
                      ].map((e, i) => (
                        <tr key={e.code} className={`border-t border-[var(--rule)] ${i % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/50'}`}>
                          <td className="px-3 py-1.5 font-mono text-[var(--text-sec)]">{e.status}</td>
                          <td className="px-3 py-1.5 font-mono text-[var(--coral)]">{e.code}</td>
                          <td className="px-3 py-1.5 text-[var(--text-sec)]">{e.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Health check */}
              <div className="border border-[var(--rule)] p-4">
                <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Test Connectivity (no auth required)</p>
                <div className="relative bg-[var(--bg)] border border-[var(--rule)]">
                  <div className="absolute top-2 right-2">
                    <CopyButton text="curl https://<project>.supabase.co/functions/v1/api/health" />
                  </div>
                  <pre className="text-xs text-[var(--text-ter)] px-3 py-2 font-mono">curl https://&lt;project&gt;.supabase.co/functions/v1/api/health</pre>
                </div>
              </div>

              {/* Pagination */}
              <div className="border border-[var(--rule)] p-4">
                <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Pagination</p>
                <p className="text-sm text-[var(--text-sec)]">List endpoints support offset pagination via <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">page</code> and <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">per_page</code> (max 100). Paginated responses include a <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">pagination</code> envelope with <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">page</code>, <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">per_page</code>, <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">total</code>, <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">total_pages</code>.</p>
              </div>
            </div>
          </section>

          {/* Resource sections */}
          {sections.slice(1).map((section) => (
            <section key={section.id} id={`api-section-${section.id}`}>
              <div className="mb-3">
                <h2 className="text-base font-semibold text-[var(--text-pri)] font-heading">{section.label}</h2>
                <p className="text-sm text-[var(--text-ter)] mt-0.5">{section.description}</p>
              </div>
              <div className="space-y-2">
                {section.endpoints.map((ep, i) => (
                  <EndpointCard key={`${ep.method}-${ep.path}-${i}`} endpoint={ep} />
                ))}
              </div>
            </section>
          ))}

          {/* Scopes reference */}
          <section id="api-section-scopes">
            <h2 className="text-base font-semibold text-[var(--text-pri)] font-heading mb-3">Scopes Reference</h2>
            <div className="border border-[var(--rule)] overflow-hidden">
              <table className="w-full text-xs">
                <thead><tr className="bg-[var(--surface)]"><th className="text-left px-4 py-2 font-semibold text-[var(--text-sec)]">Scope</th><th className="text-left px-4 py-2 font-semibold text-[var(--text-sec)]">Grants access to</th></tr></thead>
                <tbody>
                  {[
                    ['items:read', 'Read item records, attributes, relationships'],
                    ['items:write', 'Create, update, delete items and attributes'],
                    ['sessions:read', 'Read session records and results'],
                    ['sessions:write', 'Create, update, transition sessions'],
                    ['tool_sessions:read', 'Read tool sessions and responses'],
                    ['tool_sessions:write', 'Create tool sessions, upsert participant responses via /responses, finalize'],
                    ['cycles:read', 'Read cycle records and track instances'],
                    ['cycles:write', 'Create and update cycles'],
                    ['catchball:read', 'Read catchball loops and proposals'],
                    ['catchball:write', 'Open loops, send requests, submit/accept proposals'],
                    ['reports:read', 'Access all report endpoints'],
                    ['webhooks:read', 'List webhook endpoints'],
                    ['webhooks:write', 'Create and delete webhook endpoints'],
                    ['workspace:read', 'Read workspace metadata'],
                    ['workspace:admin', 'Manage API keys, workspace settings'],
                  ].map(([scope, desc], i) => (
                    <tr key={scope} className={`border-t border-[var(--rule)] ${i % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/50'}`}>
                      <td className="px-4 py-2 font-mono text-[var(--text-ter)]">{scope}</td>
                      <td className="px-4 py-2 text-[var(--text-sec)]">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Footer */}
          <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-[var(--text-pri)]">Canonical documentation</p>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">Full endpoint detail, curl examples, and guides on the docs site.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={publicDocsPath('/api')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary-700 bg-[var(--bg-raised)] border border-primary-200 px-3 py-1.5 hover:bg-primary-50 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Docs: API overview
              </a>
              <Link
                to="/prioritization/settings/api-keys"
                className="inline-flex items-center gap-1.5 text-sm text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] px-3 py-1.5 hover:bg-[var(--hover-tint)] transition-colors"
              >
                <Key className="h-3.5 w-3.5" />
                Manage API keys
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
